import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  Divider,
  Paper,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack
} from '@mui/material';
import { HelpOutline, Close, EditNote, Warning } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { DraggableDialog } from '../../../shared/components/DraggableDialog';
import EditPanelAlert from '../components/EditPanelAlert';
import { useSectionStyles } from '@/styles/utilities/hooks';
import { useUsersData } from '@/shared/hooks/useUsersData';

const MetadataEditingPanel = forwardRef(({ 
  selectedRows = [], 
  uploadFiles = [],
  selectedUploadRows = [],
  onPreviewUpdate,
  previewMode = false,
  allPrepFiles = [] // All prep files for autocomplete suggestions
}, ref) => {
  // Fetch user data for author suggestions
  const { usernames } = useUsersData();
  
  // State for each editable field
  const [fieldValues, setFieldValues] = useState({
    project: '',
    target: '',
    mutation: '',
    subgroup: '',
    workflow: '',
    batch: '',
    ligand: '',
    ligand_tag: '',
    author: '',
    prepared: '',
    notes: '',
    origin: ''
  });

  // State to track which fields have varied values
  const [variedFields, setVariedFields] = useState(new Set());
  
  // State to track which fields have been modified by user
  const [modifiedFields, setModifiedFields] = useState(new Set());
  
  // State to track original field values when rows are selected
  const [originalFieldValues, setOriginalFieldValues] = useState({});
  
  // State for help dialog
  const [showHelp, setShowHelp] = useState(false);
  
  // State for unsaved changes dialog
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Calculate total selected
  const totalSelected = selectedRows.length + selectedUploadRows.length;
  
  // Generate autocomplete suggestions from existing data with hierarchical filtering
  const suggestions = useMemo(() => {
    const allFiles = [...allPrepFiles, ...uploadFiles];
    
    const extractUniqueValues = (field, filters = {}) => {
      const values = new Set();
      allFiles.forEach(file => {
        // Apply all filters if specified
        for (const [filterField, filterValue] of Object.entries(filters)) {
          if (filterValue && file[filterField] !== filterValue) {
            return;
          }
        }
        
        const value = file[field];
        if (value && value !== '' && value !== '<none>') {
          values.add(value);
        }
      });
      return Array.from(values).sort();
    };
    
    // Get current field values for filtering
    const currentProject = fieldValues.project && fieldValues.project !== '<varied>' ? fieldValues.project : null;
    const currentTarget = fieldValues.target && fieldValues.target !== '<varied>' ? fieldValues.target : null;
    
    return {
      // No filtering for project (top level)
      project: extractUniqueValues('project'),
      
      // Target filtered by selected project
      target: currentProject 
        ? extractUniqueValues('target', { project: currentProject })
        : extractUniqueValues('target'),
      
      // Mutation filtered by selected target (and implicitly by project)
      mutation: currentTarget
        ? extractUniqueValues('mutation', { target: currentTarget })
        : extractUniqueValues('mutation'),
      
      // Subgroup filtered by selected project
      subgroup: currentProject
        ? extractUniqueValues('subgroup', { project: currentProject })
        : extractUniqueValues('subgroup'),
      
      // Origin filtered by selected target
      origin: currentTarget
        ? extractUniqueValues('origin_name', { target: currentTarget })
        : extractUniqueValues('origin_name'),
      
      // Workflow remains unfiltered (global)
      workflow: extractUniqueValues('prep_workflow'),
      
      // Batch filtered by BOTH project AND target
      batch: currentProject || currentTarget
        ? extractUniqueValues('batch', { 
            ...(currentProject && { project: currentProject }),
            ...(currentTarget && { target: currentTarget })
          })
        : extractUniqueValues('batch'),
      
      // Ligand name filtered by target
      ligand: currentTarget
        ? extractUniqueValues('ligand_name', { target: currentTarget })
        : extractUniqueValues('ligand_name'),
      
      // Ligand tag filtered by target
      ligand_tag: currentTarget
        ? extractUniqueValues('ligand_tag', { target: currentTarget })
        : extractUniqueValues('ligand_tag'),
      
      // Author combines existing values with all user full names
      author: (() => {
        const existingAuthors = extractUniqueValues('author');
        const userFullNames = Object.values(usernames || {}).filter(name => name && name !== '');
        const combined = new Set([...existingAuthors, ...userFullNames]);
        return Array.from(combined).sort();
      })(),
      
      // Notes remains unfiltered (global)
      notes: extractUniqueValues('notes')
    };
  }, [allPrepFiles, uploadFiles, fieldValues.project, fieldValues.target, usernames]);
  
  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    reset: () => {
      setFieldValues({
        project: '',
        target: '',
        mutation: '',
        subgroup: '',
        workflow: '',
        batch: '',
        ligand: '',
        ligand_tag: '',
        author: '',
        prepared: '',
        notes: '',
        origin: ''
      });
      setVariedFields(new Set());
      setModifiedFields(new Set());
      setOriginalFieldValues({});
    },
    toggleHelp: () => {
      setShowHelp(prev => !prev);
    },
    hasUnsavedChanges: () => {
      return modifiedFields.size > 0;
    },
    confirmUnsavedChanges: (action) => {
      if (modifiedFields.size > 0) {
        setPendingAction(action);
        setShowUnsavedDialog(true);
        return false; // Prevent immediate action
      }
      return true; // No unsaved changes, allow action
    },
    discardChanges: () => {
      setModifiedFields(new Set());
      setFieldValues(originalFieldValues);
    }
  }), [modifiedFields, originalFieldValues]);
  
  // Analyze selected rows to determine field values
  useEffect(() => {
    if (totalSelected === 0) {
      // Clear everything when no selection
      setFieldValues({
        project: '',
        target: '',
        mutation: '',
        subgroup: '',
        workflow: '',
        batch: '',
        ligand: '',
        ligand_tag: '',
        author: '',
        prepared: '',
        notes: '',
        origin: ''
      });
      setVariedFields(new Set());
      setModifiedFields(new Set());
      setOriginalFieldValues({});
      return;
    }
    
    // Combine all selected rows
    const allSelectedRows = [...selectedRows, ...selectedUploadRows];
    
    // Analyze each field
    const newFieldValues = {};
    const newVariedFields = new Set();
    
    const fields = [
      { key: 'project', apiField: 'project' },
      { key: 'target', apiField: 'target' },
      { key: 'mutation', apiField: 'mutation' },
      { key: 'subgroup', apiField: 'subgroup' },
      { key: 'workflow', apiField: 'prep_workflow' },
      { key: 'batch', apiField: 'batch' },
      { key: 'ligand', apiField: 'ligand_name' },
      { key: 'ligand_tag', apiField: 'ligand_tag' },
      { key: 'author', apiField: 'author' },
      { key: 'prepared', apiField: 'prepared_at' },
      { key: 'notes', apiField: 'notes' },
      { key: 'origin', apiField: 'origin_name' }
    ];
    
    fields.forEach(({ key, apiField }) => {
      const values = new Set();
      
      allSelectedRows.forEach(row => {
        let value = row[apiField];
        
        // Special handling for date fields - strip time portion
        if (apiField === 'prepared_at' && value) {
          // If it's a datetime string with T, extract just the date part
          if (value.includes('T')) {
            value = value.split('T')[0];
          }
        }
        
        // Convert null, undefined, empty string, and placeholder values to empty for comparison
        if (value && value !== '' && value !== '<none>') {
          values.add(value);
        }
      });
      
      if (values.size === 0) {
        // All values are empty/null
        newFieldValues[key] = '';
      } else if (values.size === 1) {
        // All non-empty values are the same
        newFieldValues[key] = Array.from(values)[0];
      } else {
        // Multiple different values
        newFieldValues[key] = '<varied>';
        newVariedFields.add(key);
      }
    });
    
    setFieldValues(newFieldValues);
    setVariedFields(newVariedFields);
    setOriginalFieldValues(newFieldValues); // Store original values
    // Reset modified fields when selection changes - only user edits should be bold
    setModifiedFields(new Set());
  }, [selectedRows, selectedUploadRows, totalSelected]);
  
  // Apply changes when field values change
  useEffect(() => {
    if (totalSelected > 0) {
      if (modifiedFields.size > 0) {
        const extractedData = [];
        
        // Process selected prepared PDB files
        selectedRows.forEach(row => {
        const updates = { id: row.id };
        
        // Only apply modified fields
        modifiedFields.forEach(fieldKey => {
          const value = fieldValues[fieldKey];
          // Only apply if not the <varied> placeholder
          if (value !== '<varied>') {
            // Map field keys to API fields
            const fieldMap = {
              project: 'project',
              target: 'target',
              mutation: 'mutation',
              subgroup: 'subgroup',
              workflow: 'prep_workflow',
              batch: 'batch',
              ligand: 'ligand_name',
              ligand_tag: 'ligand_tag',
              author: 'author',
              prepared: 'prepared_at',
              notes: 'notes',
              origin: 'origin_name'
            };
            
            const apiField = fieldMap[fieldKey];
            if (apiField) {
              // Handle date formatting for prepared_at
              if (apiField === 'prepared_at' && value) {
                // Check various date formats
                if (/^\d{6}$/.test(value)) {
                  // YYMMDD format
                  const yy = value.substring(0, 2);
                  const mm = value.substring(2, 4);
                  const dd = value.substring(4, 6);
                  const yyyy = parseInt(yy) <= 50 ? '20' + yy : '19' + yy;
                  updates[apiField] = `${yyyy}-${mm}-${dd}`;
                } else if (/^\d{8}$/.test(value)) {
                  // YYYYMMDD format
                  const yyyy = value.substring(0, 4);
                  const mm = value.substring(4, 6);
                  const dd = value.substring(6, 8);
                  updates[apiField] = `${yyyy}-${mm}-${dd}`;
                } else {
                  updates[apiField] = value;
                }
              } else {
                updates[apiField] = value || null;
              }
            }
          }
        });
        
        extractedData.push(updates);
      });
      
      // Process selected upload files
      selectedUploadRows.forEach(row => {
        const updates = { id: row.id };
        
        // Only apply modified fields
        modifiedFields.forEach(fieldKey => {
          const value = fieldValues[fieldKey];
          // Only apply if not the <varied> placeholder
          if (value !== '<varied>') {
            // Map field keys to API fields
            const fieldMap = {
              project: 'project',
              target: 'target',
              mutation: 'mutation',
              subgroup: 'subgroup',
              workflow: 'prep_workflow',
              batch: 'batch',
              ligand: 'ligand_name',
              ligand_tag: 'ligand_tag',
              author: 'author',
              prepared: 'prepared_at',
              notes: 'notes',
              origin: 'origin_name'
            };
            
            const apiField = fieldMap[fieldKey];
            if (apiField) {
              // Handle date formatting for prepared_at
              if (apiField === 'prepared_at' && value) {
                // Check various date formats
                if (/^\d{6}$/.test(value)) {
                  // YYMMDD format
                  const yy = value.substring(0, 2);
                  const mm = value.substring(2, 4);
                  const dd = value.substring(4, 6);
                  const yyyy = parseInt(yy) <= 50 ? '20' + yy : '19' + yy;
                  updates[apiField] = `${yyyy}-${mm}-${dd}`;
                } else if (/^\d{8}$/.test(value)) {
                  // YYYYMMDD format
                  const yyyy = value.substring(0, 4);
                  const mm = value.substring(4, 6);
                  const dd = value.substring(6, 8);
                  updates[apiField] = `${yyyy}-${mm}-${dd}`;
                } else {
                  updates[apiField] = value;
                }
              } else {
                updates[apiField] = value || null;
              }
            }
          }
        });
        
        extractedData.push(updates);
      });
      
        // Send preview data to parent, indicating source is 'metadata'
        onPreviewUpdate && onPreviewUpdate(true, extractedData, 'metadata');
      } else {
        // Clear preview when no modifications
        onPreviewUpdate && onPreviewUpdate(false, [], 'metadata');
      }
    } else {
      // Clear preview when no selection
      onPreviewUpdate && onPreviewUpdate(false, [], 'metadata');
    }
  }, [fieldValues, modifiedFields, totalSelected, selectedRows, selectedUploadRows, onPreviewUpdate]);
  
  // Handle unsaved changes dialog actions
  const handleSaveAndContinue = () => {
    // The save action should be handled by parent component
    // For now, just close dialog and execute pending action
    setShowUnsavedDialog(false);
    if (pendingAction) {
      pendingAction.onSave && pendingAction.onSave();
      pendingAction.callback && pendingAction.callback();
    }
    setPendingAction(null);
  };
  
  const handleDiscardAndContinue = () => {
    // Discard changes and execute pending action
    setModifiedFields(new Set());
    setFieldValues(originalFieldValues);
    setShowUnsavedDialog(false);
    if (pendingAction) {
      pendingAction.callback && pendingAction.callback();
    }
    setPendingAction(null);
  };
  
  const handleCancelAction = () => {
    // Just close dialog, don't execute pending action
    setShowUnsavedDialog(false);
    setPendingAction(null);
  };
  
  const handleFieldChange = (fieldKey, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
    
    // Check if the value is different from the original
    const originalValue = originalFieldValues[fieldKey] || '';
    if (value !== originalValue) {
      // Mark field as modified if different from original
      setModifiedFields(prev => new Set([...prev, fieldKey]));
    } else {
      // Remove from modified if value reverted to original
      setModifiedFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
    }
    
    // Remove from varied set if user is typing
    if (variedFields.has(fieldKey) && value !== '<varied>') {
      setVariedFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
    }
  };
  
  const renderTextField = (fieldKey, label) => {
    const isVaried = variedFields.has(fieldKey);
    const value = fieldValues[fieldKey];
    const isModified = modifiedFields.has(fieldKey);
    const fieldSuggestions = suggestions[fieldKey] || [];
    
    // Define placeholder examples for each field
    const placeholderExamples = {
      project: 'e.g., QNI123-T2DM, QNE281-PDE5',
      target: 'e.g., KRAS, BRAF, EGFR',
      mutation: 'e.g., G12C, V600E, L858R',
      subgroup: 'e.g., Unseen, Adagrasib-variants',
      origin: 'e.g., 6OIM, 4DJW, PDB123',
      workflow: 'e.g., Standard, Ver2, Ver1_MMGBSA',
      batch: 'e.g., Set1, Set1_mod, Set2_poses',
      ligand: 'e.g., Sotorasib, Lazertinib',
      ligand_tag: 'e.g., Flipped, Pose-6922, Frame-13214',
      author: 'e.g., Paul Janssen, AstraZeneca',
      prepared: 'YYYY-MM-DD, YYYYMMDD, or YYMMDD',
      notes: 'e.g., High quality structure, needs review'
    };
    
    const placeholder = isVaried && !isModified ? 'Enter value to apply to all' : 
                       (!value || value === '') ? placeholderExamples[fieldKey] || '' : '';
    
    // All fields use Autocomplete for consistency, including Notes
    return (
      <Autocomplete
        fullWidth
        size="small"
        options={fieldSuggestions}
        value={value || ''}
        noOptionsText="No options"
        onInputChange={(event, newValue) => {
          handleFieldChange(fieldKey, newValue);
        }}
        freeSolo
        disabled={totalSelected === 0}
        renderOption={(props, option) => (
          <li {...props} style={{ fontSize: theme.typography.fontSize.base }}>
            {option}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: true
            }}
            InputProps={{
              ...params.InputProps,
              sx: {
                ...(value === '<varied>' && {
                  fontStyle: 'italic',
                  color: theme.palette.text.secondary
                }),
                ...(isModified && value !== '<varied>' && {
                  fontWeight: theme.typography.fontWeight.semibold
                })
              }
            }}
          />
        )}
      />
    );
  };

  const theme = useTheme();
  const sectionStyles = useSectionStyles();
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Help Dialog - Draggable */}
      <DraggableDialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
        title="Metadata Editing Help"
        variant="help"
        maxWidth="sm"
        fullWidth
      >
          <Box sx={{ 
            '& h3': { fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, mt: theme.spacing(2), mb: theme.spacing(1) },
            '& h3:first-of-type': { mt: 0 },
            '& ul': { pl: theme.spacing(2), my: theme.spacing(0.5) },
            '& li': { fontSize: theme.typography.fontSize.xs, mb: theme.spacing(0.5) },
            '& code': { 
              backgroundColor: theme.palette.grey[200], 
              px: theme.spacing(0.5), 
              py: theme.spacing(0.2), 
              borderRadius: theme.spacing(0.5),
              fontSize: theme.typography.fontSize.xs,
              fontFamily: 'monospace'
            },
            '& p': { fontSize: theme.typography.fontSize.xs, my: theme.spacing(0.5) },
            '& i.material-icons': {
              fontSize: `${theme.dimensions.icon.small}px !important`,
              verticalAlign: 'middle',
              marginRight: theme.spacing(0.25)
            }
          }}>
            <ReactMarkdown rehypePlugins={[rehypeRaw]}>
{`### 1. Find and Select Files to Update

- Use the Browser <i class="material-icons" style="font-size: 14px; vertical-align: middle;">account_tree</i> to filter by project/target/batch
- Use the search field above the table to filter files by text
- Files in the upload panel can also be selected and updated
- Selection shortcuts:
  - \`Click\` — Select single file
  - \`Shift\`+\`Click\` — Select range of files  
  - \`Ctrl\`/\`Cmd\`+\`Click\` — Add/remove individual files
  - \`Ctrl\`/\`Cmd\`+\`A\` — Select all visible files

### 2. Edit Metadata Fields

**How fields work with multiple selections:**

- Fields display the common value when all selected files have the same metadata
- Fields show \`<varied>\` when selected files have different values for that field
- Type a new value in any field to update all selected files simultaneously
  - **Note:** This sets the same value for all selected files. For extracting different values from file paths, consider using Pattern Extraction
- Modified fields appear in **bold** to track your changes before saving
- Autocomplete dropdowns offer existing values from your database

### 3. Field Descriptions

**Structure Information:**
- **Project** — Research project or study identifier (e.g., QNI123-T2DM)
- **Target** — Protein target name (e.g., KRAS, BRAF, EGFR)
- **Mutation** — Specific mutation variant (e.g., G12C, V600E)
- **Origin** — Source structure ID from PDB or custom/private database (e.g., 6OIM, 4DJW, CUSTOM-001)
- **Subgroup** — Label to distinguish different ligand sets (e.g., Unseen, Control, Set-A)
- **Ligand Name** — Compound name or identifier (e.g., Sotorasib, COMPOUND-123)
- **Ligand Tag** — Specific pose or variant identifier (e.g., Pose-6922)

**Processing Information:**
- **Workflow** — Preparation protocol version (e.g., Standard, Ver2)
- **Batch** — Processing batch identifier (e.g., Set1, Set1_mod)
- **Notes** — Additional comments or observations
- **Author** — Person or organization who prepared the structure
- **Prepared Date** — Date of structure preparation (YYYY-MM-DD)

### 4. Save or Cancel

- Preview appears in the main table for existing files
- Click the "Save" button (top-right of main table) to apply metadata changes
- Click "Cancel" to discard edits and clear preview
- For files in upload panel, click "Upload" (top-right) to upload with edited metadata`}
            </ReactMarkdown>
          </Box>
      </DraggableDialog>
      
      {/* Selection Info */}
      <EditPanelAlert 
        selectedRows={selectedRows}
        selectedUploadRows={selectedUploadRows}
        variedFields={variedFields}
      />

      {/* Scrollable Content Container */}
      <Box sx={{ flex: 1, overflow: 'auto', px: theme.spacing(2), pb: theme.spacing(2) }}>
        {/* Structure Information */}
        <Typography variant="caption" sx={{ ...sectionStyles.sectionHeader, mb: theme.spacing(1) }}>
          STRUCTURE INFORMATION
        </Typography>
        <Stack spacing={1}>
          {renderTextField('project', 'Project')}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing(1) }}>
            {renderTextField('target', 'Target')}
            {renderTextField('mutation', 'Mutation')}
          </Box>
          {renderTextField('origin', 'Origin')}
          {renderTextField('subgroup', 'Subgroup')}
          {renderTextField('ligand', 'Ligand Name')}
          {renderTextField('ligand_tag', 'Ligand Tag')}
        </Stack>
        
        {/* Processing Information */}
        <Box sx={{ mt: theme.spacing(2) }}>
          <Typography variant="caption" sx={{ ...sectionStyles.sectionHeader, mb: theme.spacing(1) }}>
            PROCESSING INFORMATION
          </Typography>
          <Stack spacing={1}>
            {renderTextField('workflow', 'Workflow')}
            {renderTextField('batch', 'Batch')}
            {renderTextField('notes', 'Notes')}
            {renderTextField('author', 'Author')}
            {renderTextField('prepared', 'Prepared Date')}
          </Stack>
        </Box>
      </Box>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog
        open={showUnsavedDialog}
        onClose={handleCancelAction}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <Warning color="warning" sx={{ fontSize: theme.dimensions.icon.medium }} />
          <Typography sx={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.medium }}>Unsaved Changes</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: theme.typography.fontSize.base }}>
            You have unsaved changes in the Metadata Editing panel. What would you like to do?
          </Typography>
          <Box sx={{ mt: theme.spacing(2), p: theme.spacing(2), bgcolor: theme.palette.grey[50], borderRadius: theme.spacing(1) }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: theme.typography.fontSize.sm }}>
              Modified fields: {Array.from(modifiedFields).join(', ')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDiscardAndContinue} color="error">
            Discard Changes
          </Button>
          <Button onClick={handleSaveAndContinue} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
});

MetadataEditingPanel.displayName = 'MetadataEditingPanel';

export default MetadataEditingPanel;